import React from 'react';
import { trackEvent } from '../../../services/utils/tracking/tracking.service';
import { CircularProgress } from '@material-ui/core';
import { cn } from '../../../../utils/cn';

/**
 * ButtonType represents different button types (success, warning, danger, default)
 */
export enum ButtonType {
  success = 'success',
  warning = 'warning',
  danger = 'danger',
  default = 'default',
  primary = 'primary',
  primaryInverted = 'primary-inverted',
  gray = 'gray',
  stopRecord = 'stop-record',
  transparent = 'transparent'
}

/**
 * ButtonSize represents different button sizes (small, medium, large)
 */
export enum ButtonSize {
  small = 'small',
  medium = 'medium',
  large = 'large',
  full = 'full'
}

/**
 * ButtonProps interface
 * - tracking: object (button event name for tracking)
 * - children: React.ReactNode (children that renders inside button)
 * - type: ButtonType (define button type, "default" is the default one)
 * - size: ButtonSize (define button size, "medium" is the default one)
 * - disabled: boolean (is button disabled or not)
 * - onClick: Function (callback function that triggers when click on button)
 */
export interface ButtonProps {
  tracking?: {
    action: string;
    category: string;
    label: string;
  };
  children: React.ReactNode;
  type?: ButtonType;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  rounded?: boolean;
  buttonName?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onMouseUp?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  className?: string;
  pulseEffect?: boolean;
  classNameButton?: string;
}

/**
 * Button component renders a button with different colors and sizes
 * based on button type/size/disabled status, trigger event tracking with action, category and label
 * is provided and onClick callback function
 * @param tracking: objecy (button event name for tracking)
 * @param children: React.ReactNode (children that renders inside button)
 * @param type: ButtonType (define button type, "default" is the default one)
 * @param size: ButtonSize (define button size, "medium" is the default one)
 * @param disabled: boolean (is button disabled or not)
 * @param onClick: Function (callback function that triggers when click on button)
 */
const Button: React.FC<ButtonProps> = ({
  tracking,
  children,
  type = ButtonType.default,
  size = ButtonSize.medium,
  disabled,
  loading = false,
  rounded = false,
  className,
  buttonName,
  onClick,
  onMouseDown,
  onMouseUp,
  pulseEffect,
  classNameButton = ''
}) => {
  const baseContainerClass = 'relative flex flex-col items-center justify-center';
  const baseButtonClass =
    'relative cursor-pointer select-none rounded-lg border font-medium text-white transition-all duration-200 ease-in-out focus:outline-none';

  const disabledClass =
    'disabled:cursor-not-allowed disabled:pointer-events-none disabled:bg-gray-200 disabled:border-gray-200 disabled:text-creams-800';

  const sizeClasses: Record<ButtonSize, { container: string; button: string }> = {
    [ButtonSize.small]: {
      container: '',
      button: cn(
        'px-[2.25rem] py-[1rem] text-[1rem] leading-[1rem]',
        rounded && 'h-[4rem] w-[4rem] rounded-full p-0 leading-[4rem]'
      )
    },
    [ButtonSize.medium]: {
      container: '',
      button: cn(
        'px-[3.75rem] py-[1.25rem] text-[1.125rem] leading-[1.25rem]',
        rounded && 'h-[5.625rem] w-[5.625rem] rounded-full p-0 text-[5rem]'
      )
    },
    [ButtonSize.large]: {
      container: '',
      button: cn(
        'px-[2.5rem] py-[1.5rem] text-[1.25rem] leading-[1.5rem]',
        rounded && 'h-[6.5rem] w-[6.5rem] rounded-full p-0 md:h-[8.125rem] md:w-[8.125rem]'
      )
    },
    [ButtonSize.full]: {
      container: 'w-full',
      button: 'm-0 block h-[5.2rem] w-full px-[2.5rem] text-[1.25rem] leading-[1.25rem]'
    }
  };

  const typeClasses: Record<ButtonType, string> = {
    [ButtonType.default]:
      'bg-primary-500 border-primary-500 hover:brightness-95 active:brightness-90',
    [ButtonType.primary]:
      'bg-primary-500 border-primary-500 hover:brightness-95 active:brightness-90',
    [ButtonType.success]:
      'bg-action-success border-action-success hover:brightness-95 active:brightness-90',
    [ButtonType.warning]:
      'bg-action-alert border-action-alert hover:brightness-95 active:brightness-90',
    [ButtonType.danger]: 'bg-scarlet border-scarlet hover:brightness-95 active:brightness-90',
    [ButtonType.gray]:
      'bg-gray-200 border-2 border-gray-200 text-neutral-400 hover:bg-primary-500 hover:border-primary-500 hover:text-white active:bg-primary-500 active:border-primary-500 active:text-white',
    [ButtonType.primaryInverted]:
      'bg-white border-[#37454d] !text-neutral-500  hover:border-primary-600 active:border-primary-700',
    [ButtonType.stopRecord]: 'bg-primary-50 border-primary-50 text-scarlet hover:bg-primary-600',
    [ButtonType.transparent]: 'bg-transparent border-transparent '
  };

  const pulseClasses =
    'shadow-[0_0_0_0_rgba(232,76,61,0.7)] border-scarlet bg-scarlet text-white transition-all duration-300 ease-in-out hover:shadow-[0_0_0_0_rgba(232,76,61,0.7)] focus:shadow-[0_0_0_0_rgba(232,76,61,0.7)] hover:brightness-100 active:brightness-100';

  function handleClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (!tracking) {
      console.warn('No tracking value is provided!');
    } else {
      trackEvent(tracking.action, { category: tracking.category, label: tracking.label });
    }
    if (!disabled && onClick) {
      onClick(event);
    }
  }
  function handleMouseDown(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (!tracking) {
      console.warn('No tracking value is provided!');
    } else {
      trackEvent(tracking.action, { category: tracking.category, label: tracking.label });
    }
    if (!disabled && onMouseDown) {
      onMouseDown(event);
    }
  }
  function handleMouseUp(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (!tracking) {
      console.warn('No tracking value is provided!');
    } else {
      trackEvent(tracking.action, { category: tracking.category, label: tracking.label });
    }
    if (!disabled && onMouseUp) {
      onMouseUp(event);
    }
  }

  return (
    <div className={cn(baseContainerClass, sizeClasses[size].container, className)}>
      <button
        className={cn(
          baseButtonClass,
          disabledClass,
          sizeClasses[size].button,
          typeClasses[type],
          pulseEffect && pulseClasses,
          classNameButton
        )}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        disabled={disabled}
      >
        {loading ? (
          <div className="text-white">
            <CircularProgress className="mt-[2px]" size={24} color="inherit" />
          </div>
        ) : (
          <span> {children} </span>
        )}
      </button>
      {buttonName && <span className="mt-2">{buttonName}</span>}
    </div>
  );
};

export default Button;
